import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api, TOKEN_KEY } from '../lib/api';

const AuthContext = createContext(null);
const FAV_KEY = 'dealrun_favorites';
const readLocalFavs = () => { try { return JSON.parse(localStorage.getItem(FAV_KEY) || '[]'); } catch { return []; } };

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(undefined);
  const [localFavs, setLocalFavs] = useState(readLocalFavs);
  const [authModalOpen, setAuthModalOpen] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) return setUser(null);
    api.get('/auth/me')
      .then((r) => setUser(r.data))
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setUser(null); });
  }, []);

  const applyAuth = async (data) => {
    localStorage.setItem(TOKEN_KEY, data.access_token);
    const pending = readLocalFavs();
    let merged = data.user;
    if (pending.length) {
      try {
        const r = await api.post('/auth/me/favorites/merge', { ids: pending });
        merged = { ...data.user, favorites: r.data.favorites };
      } catch { /* keep server favorites */ }
      localStorage.removeItem(FAV_KEY);
      setLocalFavs([]);
    }
    setUser(merged);
  };

  const login = async (email, password) => applyAuth((await api.post('/auth/login', { email, password })).data);
  const register = async (name, email, password) => applyAuth((await api.post('/auth/register', { name, email, password })).data);

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch (_) {}
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  const toggleFavorite = useCallback(async (catalogId) => {
    if (!localStorage.getItem(TOKEN_KEY)) {
      setLocalFavs((f) => {
        const next = f.includes(catalogId) ? f.filter((i) => i !== catalogId) : [...f, catalogId];
        localStorage.setItem(FAV_KEY, JSON.stringify(next));
        return next;
      });
      return;
    }
    const { data } = await api.post(`/auth/me/favorites/${catalogId}`);
    setUser((u) => ({ ...u, favorites: data.favorites }));
  }, []);

  const toggleFollow = useCallback(async (brandId) => {
    const { data } = await api.post(`/auth/me/follow/${brandId}`);
    setUser((u) => ({ ...u, followed_brands: data.followed_brands }));
  }, []);

  const markPourToiSeen = useCallback(async () => {
    const { data } = await api.post('/auth/me/seen-pour-toi');
    setUser((u) => ({ ...u, pour_toi_seen_at: data.pour_toi_seen_at }));
  }, []);

  const saveInterests = useCallback(async (interests) => {
    const { data } = await api.put('/auth/me/interests', { interests });
    setUser(data);
  }, []);

  const favorites = user ? user.favorites || [] : localFavs;
  const value = {
    user, favorites, isAdmin: user?.role === 'admin', login, register, logout, toggleFavorite, toggleFollow, saveInterests, markPourToiSeen,
    authModalOpen, openAuth: () => setAuthModalOpen(true), closeAuth: () => setAuthModalOpen(false),
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
